import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ApiResponse } from '@pm/shared';
import { apiClient } from '../client';

/**
 * Benutzerverwaltung (T-075) — CRUD gegen die employee-api über den BFF-Raw-Proxy
 * `/api/v1/employee-api/*` mit dem eingeloggten PHQ-User-Token (kein Service-Key im
 * Bundle; employee-api gated per requirePermission). X-Tenant-Id + Bearer setzt der
 * apiClient automatisch aus dem AuthContext.
 *
 * Anlegen ist ein 1-Call-Flow: POST erzeugt Mitarbeiter + Keycloak-User (Passwort
 * server-seitig, mail-frei) + Frontend-Freigabe `PHQ` zusammen. Der Nutzer fordert
 * sein Passwort danach selbst über „Passwort vergessen" an.
 */

const BASE = '/api/v1/employee-api/api/v1/employees';

/** Frontend-Code für PHQ (Registry-Seed in der employee-api). */
export const PHQ_FRONTEND_CODE = 'PHQ';

export interface AdminUser {
  id: string;
  first_name: string;
  last_name: string;
  email_primary: string;
  display_name: string;
  personal_number: string | null;
  status: string;
  frontends: string[];
  /**
   * Letzte Anmeldung als ISO-Zeitstempel, `null` wenn der Dienst keine kennt.
   *
   * Quelle ist idmanager-v2 (`lastLoginAt`), durchgereicht von der employee-api.
   * 🔴 Gemessen am 18.09.2026 auf PROD: Von 188 Benutzern tragen dort nur 12 ein
   * `lastLoginAt`, aber 98 einen Zugriffszaehler ueber null — das Feld wird also
   * NICHT bei jeder Anmeldung geschrieben. Deshalb steht `access_count` daneben
   * und nicht anstelle: Eine leere Zelle heißt „keine Anmeldung bekannt", nicht
   * „nie angemeldet".
   */
  last_login_at: string | null;
  /** Zähler aus idmanager-v2 (`accessCount`), `null` wenn unbekannt. */
  access_count: number | null;
  /**
   * Zuordnung zum Benutzer des Identitätsdienstes: `'idm_user_id'`, `'email'` oder
   * `null` (nicht zugeordnet). Trennt „hat keine Anmeldedaten" von „konnte nicht
   * zugeordnet werden" — beides sähe in der Tabelle sonst gleich aus.
   */
  idm_matched_by: 'idm_user_id' | 'email' | null;
  /**
   * Ob der Dienst zu diesem Mitarbeiter überhaupt etwas zum Identitätsdienst gesagt
   * hat. 🔴 Der Unterschied zu `idm_matched_by === null` ist keine Feinheit: Fehlt das
   * `idm`-Objekt, wissen wir NICHTS — dann ist „nicht zugeordnet" eine Behauptung über
   * eine Person, die auf einer Antwort beruht, die es nie gab. Erst wenn der Dienst
   * geantwortet UND keine Zuordnung gefunden hat, ist die Aussage eine Aussage.
   */
  idm_beantwortet: boolean;
  /**
   * Zeitpunkt, zu dem dieser Mensch zuletzt IN PHQ war — ISO-Zeitstempel oder `null`.
   *
   * 🔴 Das ist die Angabe, um die es eigentlich geht, und sie ist von allen anderen
   * auf dieser Zeile verschieden:
   *   `last_login_at`      wann jemand sich zuletzt ANGEMELDET hat (Identitätsdienst)
   *   `access_count`       Zähler unbekannter Bedeutung (Identitätsdienst)
   *   `last_seen_phq_at`   wann jemand zuletzt IN PHQ war
   *
   * Der Name trägt `phq` bewusst: Hiesse das Feld nur `last_seen_at`, würde es der
   * nächste Dienst ebenfalls fortschreiben, und dann stünde dort „zuletzt irgendwo
   * gesehen" — womit die Frage wieder unbeantwortet wäre.
   */
  last_seen_phq_at: string | null;
}

interface RawAdminUser {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email_primary?: string | null;
  display_name?: string | null;
  personal_number?: string | null;
  status?: string | null;
  frontends?: string[] | null;
  /**
   * Die employee-api liefert die Felder des Identitätsdienstes verschachtelt unter
   * `idm` (Vertrag von employee, 18.09.2026). Die flachen Schreibweisen bleiben als
   * Rückfall stehen — sie kosten nichts und fangen den Fall ab, dass der Vertrag sich
   * vor der Auslieferung noch ändert.
   */
  idm?: {
    last_login_at?: string | null;
    access_count?: number | null;
    /**
     * Wie der Mitarbeitersatz dem Benutzer des Identitätsdienstes zugeordnet wurde:
     * über die Kennung, über die E-Mail, oder gar nicht. `null` heißt NICHT
     * „keine Anmeldedaten", sondern „keine Zuordnung" — ein Unterschied, den die
     * Oberfläche zeigen muss, weil er sonst wie ein leerer Wert aussieht.
     */
    matched_by?: 'idm_user_id' | 'email' | null;
  } | null;
  last_seen_phq_at?: string | null;
  last_login_at?: string | null;
  lastLoginAt?: string | null;
  access_count?: number | null;
  accessCount?: number | null;
}

/**
 * Einmalige Warnung, wenn die IDM-Felder NICHT verschachtelt unter `idm` ankommen,
 * sondern über den flachen Rückfall. Der Rückfall selbst ist gewollt — er kostet nichts
 * und deckt einen anderen Lieferanten ab. Ohne diesen Hinweis wüsste aber niemand, dass
 * er gegriffen hat, und ein abweichender Lieferant bliebe unsichtbar (employee, 18.09.2026).
 */
let rueckfallGemeldet = false;

/** Liest die IDM-Felder bevorzugt verschachtelt, sonst flach, und meldet den Rückfall einmal. */
function leseIdmFelder(u: RawAdminUser): { last_login_at: string | null; access_count: number | null } {
  if (u.idm) {
    return { last_login_at: u.idm.last_login_at ?? null, access_count: u.idm.access_count ?? null };
  }
  const flach = {
    last_login_at: u.last_login_at ?? u.lastLoginAt ?? null,
    access_count: u.access_count ?? u.accessCount ?? null,
  };
  if (!rueckfallGemeldet && (flach.last_login_at !== null || flach.access_count !== null)) {
    rueckfallGemeldet = true;
    console.warn(
      '[Benutzerverwaltung] IDM-Felder kamen flach statt unter `idm` an. ' +
      'Das deutet auf einen anderen Lieferanten oder einen älteren Stand der employee-api hin.',
    );
  }
  return flach;
}

function normalize(u: RawAdminUser): AdminUser {
  const idmFelder = leseIdmFelder(u);
  return {
    id: u.id,
    first_name: u.first_name ?? '',
    last_name: u.last_name ?? '',
    email_primary: u.email_primary ?? '',
    display_name: u.display_name ?? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim(),
    personal_number: u.personal_number ?? null,
    status: u.status ?? 'active',
    frontends: u.frontends ?? [],
    last_login_at: idmFelder.last_login_at,
    access_count: idmFelder.access_count,
    idm_matched_by: u.idm?.matched_by ?? null,
    idm_beantwortet: u.idm != null,
    // Kommt NICHT aus dem idm-Block: Das Feld gehört dem Mitarbeitersatz selbst und
    // hängt nicht am Identitätsdienst — genau deshalb trägt es auch dort, wo dessen
    // Bestand leer ist (auf STAGING: null Überschneidung, gemessen 21.09.2026).
    last_seen_phq_at: u.last_seen_phq_at ?? null,
  };
}

const KEY = ['user-admin'] as const;

/**
 * Auskunft der employee-api darüber, wie die Anreicherung aus dem Identitätsdienst
 * gelaufen ist. Liegt auf Antwortebene, neben `data` — nicht je Zeile.
 *
 * 🔴 Wozu das gebraucht wird: Zwei völlig verschiedene Lagen erzeugen in der Tabelle
 * dasselbe Bild, nämlich überall „nicht zugeordnet":
 *   matched 2 · unmatched 81   der Abgleich arbeitet, der Identitätsdienst ist fast leer
 *   matched 0 · unmatched 83   der Abgleich greift gar nicht
 * Ohne diese Zahlen sind sie in der Oberfläche nicht zu unterscheiden. Auf STAGING ist
 * der erste Fall der Normalzustand — dort liegen zwei Benutzer im Identitätsdienst
 * (Messung employee, 18.09.2026), auf PROD sind es 188 bei 205 Mitarbeitern.
 */
export interface IdmErhebung {
  /** `false` heißt: die Liste wurde unvollständig geholt. Eine fehlende Person ist
   *  dann KEIN Befund über diese Person. */
  complete: boolean;
  /** Klartext, warum unvollständig. Kein Vertrag — nie auf Textbestandteile prüfen,
   *  nur auf `complete` (ausdrücklicher Hinweis von employee). */
  reason?: string | null;
  matched?: number | null;
  matched_by_id?: number | null;
  matched_by_email?: number | null;
  unmatched?: number | null;
  /** Angaben bis zu fünf Minuten alt. */
  from_cache?: boolean | null;
}

export interface UseUsersParams {
  search?: string;
  status?: string;
}

/** Benutzerliste (aktueller Mandant via X-Tenant-Id). */
export function useUsers(params?: UseUsersParams) {
  return useQuery({
    queryKey: [...KEY, params],
    queryFn: async (): Promise<{ items: AdminUser[]; erhebung: IdmErhebung | null }> => {
      // Server-side Filter auf aktives PHQ-Mapping (INNER JOIN frontend_access,
      // revoked_at IS NULL). NICHT client-seitig nachfiltern — bräche die Pagination.
      // `include=idm` fordert die Felder des Identitätsdienstes an (letzte Anmeldung,
      // Zähler, Zuordnung). 🔴 Ohne diesen Parameter liefert die employee-api sie NICHT,
      // und zwar ohne Fehler und ohne Hinweis — die Antwort ist gültig, nur ohne das
      // `idm`-Objekt. Fehlt der Parameter, bleibt die Spalte „Aktivität" also leer und
      // sieht dabei aus wie ein Dienst, der nichts weiß (employee, 18.09.2026).
      // Läuft ein älterer Stand, der den Parameter nicht kennt, fällt er stillschweigend
      // aus dem Schema — die Abfrage bleibt gültig, die Felder fehlen wie heute.
      const query: Record<string, unknown> = { frontend: PHQ_FRONTEND_CODE, include: 'idm' };
      if (params?.search) query.search = params.search;
      if (params?.status) query.status = params.status;
      const resp = await apiClient.get<RawAdminUser[]>(BASE, query) as
        { data?: RawAdminUser[] | null; idm_enrichment?: IdmErhebung | null };
      return {
        items: (resp.data ?? []).map(normalize),
        // Fehlt der Schlüssel, hat der Dienst zur Anreicherung nichts gesagt — das ist
        // etwas anderes als eine Erhebung mit lauter Nullen und wird als `null` geführt.
        erhebung: resp.idm_enrichment ?? null,
      };
    },
    staleTime: 30_000,
  });
}

export interface CreateUserInput {
  first_name: string;
  last_name: string;
  email_primary: string;
}

export interface CreateUserResult {
  /** true = neuer Mensch angelegt (201), false = bestehender erhielt PHQ-Zugang (200 attached). */
  created: boolean;
}

/**
 * Anlegen — idempotenter „create-or-attach"-Call (employee-api v0.18.4):
 * E-Mail neu → Mitarbeiter + Keycloak-User (mail-frei) + PHQ-Freigabe (201 created).
 * E-Mail existiert bereits → KEIN Duplikat, der bestehende Mensch erhält nur das
 * PHQ-Mapping (200 attached); seine Stammdaten bleiben unangetastet.
 */
export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateUserInput): Promise<CreateUserResult> => {
      const resp = await apiClient.post<RawAdminUser>(BASE, { ...data, frontends: [PHQ_FRONTEND_CODE] }) as ApiResponse<RawAdminUser> & { created?: boolean };
      // `created` fehlt bei älteren API-Versionen → Fallback true (neutrale „angelegt"-Meldung).
      return { created: resp.created ?? true };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export interface UpdateUserInput {
  first_name?: string;
  last_name?: string;
  status?: string;
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; data: UpdateUserInput }) =>
      apiClient.patch<RawAdminUser>(`${BASE}/${vars.id}`, vars.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * PHQ-Zugang entziehen (Normalfall-„Löschen"): löst NUR das PHQ-Frontend-Mapping
 * (soft-revoke, `DELETE /:id/frontends/PHQ`). Der Mensch, sein evtl. SMS-PRO-Zugang
 * und der Keycloak-Login bleiben unberührt — employee-Records sind PHQ<->SMS-PRO
 * geteilt. Serverseitig reversibel (Re-Grant beim erneuten Anlegen).
 */
export function useRevokePhqAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`${BASE}/${id}/frontends/${PHQ_FRONTEND_CODE}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// Hard-Delete (DELETE /:id/hard) wird in PHQ BEWUSST NICHT exponiert: er entfernt den
// globalen employee-Record inkl. Keycloak-Login systemweit — das würde auch den
// SMS-PRO-Login des Menschen töten. Sicheres Offboarding erfordert ein per-User-upg-
// Signal (smspro GET /sms-pro-access/resolve?email=), das erst auf Ansage gebaut wird.
// Bis dahin bietet PHQ ausschließlich den mapping-only Revoke oben an.
